import useAppStore from "@/context/store";
import { logout } from "@/services";
import { debounce } from "lodash";
import oauth2Client from "../auth";
import {
  GDErrCode,
  HBErrCode,
  NeedReLoginCodes,
  TokenExpiredCodes,
} from "./customErrCode";
import { NormalizedError } from "./errorHandle";

const handleLogout = debounce(async () => {
  await logout(false);
  useAppStore.setState({
    user: null,
  });
}, 500);

/**
 * 处理花瓣登录失效场景
 * - 其他设备登录
 * - 登录失效401
 * @param error 错误信息
 */
export function handleHBLoginExpired(error: NormalizedError) {
  const { response, config, code } = error;
  const ext_message = response?.data?.ext_message;
  const { url } = config;

  // 其他设备登录处理
  if (
    code === HBErrCode.notLogin &&
    ext_message &&
    typeof window !== "undefined"
  ) {
    import("@/components/LoginModal/otherDeviceLogin").then(
      ({ OtherDeviceLoginInfo }) => {
        OtherDeviceLoginInfo.open(ext_message.detail);
      }
    );

    // 调整错误信息，兼容老代码处理逻辑
    error.code = error.err = HBErrCode.notLogin;
    error.message = error.msg = "登录失效，请重新登录!";

    handleLogout();

    return;
  }

  // 登录失效白名单
  const whiteList = ["/auth/", "/message/unreads"];
  // 登录失效逻辑处理
  if (!whiteList.includes(url) && code === HBErrCode.notLogin) {
    // 调整错误信息，兼容老代码处理逻辑
    error.code = error.err = HBErrCode.notLogin;
    error.message = error.msg = "登录失效，请重新登录!";

    // 清除用户信息
    handleLogout();
  }
}

export async function handleGDTokenExpired(error: NormalizedError) {
  const { response, config, code } = error;
  const { headers, options } = config;
  const { request, accessToken, times } = options || {};

  if (NeedReLoginCodes.includes(error?.code as GDErrCode)) {
    // 处理其他设备登录
    if (error?.code === GDErrCode.OtherDeviceLogin) {
      import("@/components/LoginModal/otherDeviceLogin").then(
        ({ OtherDeviceLoginInfo }) => {
          OtherDeviceLoginInfo.open(response?.data?.detail || {});
        }
      );
    }

    handleLogout();

    error.message = error.msg = "登录已失效，请重新登录";
    return;
  }

  // 如果是 SSR 流程，且 accessToken 失效，则跳转 ums 刷新 token
  // 由于 next 12 目前不支持 next/navigation 的 redirect 方法，所以暂时不处理
  // if (code === GDErrCode.InvalidAccessTokenExpire && typeof window === "undefined") {
  //   const redirectUri = encodeURIComponent(config.url);
  //   const tokenRefreshUrl = `/cgi/token-refresh?redirect_uri=${redirectUri}`;
  //   redirect(tokenRefreshUrl);
  // }

  if (TokenExpiredCodes.includes(code as GDErrCode)) {
    // accessToken 失效逻辑
    const isValid = await oauth2Client.checkTokenValid({
      invalidAuthorization: `Bearer ${accessToken}`,
    });

    if (isValid) {
      // 避免无限重试
      if (times > 3) {
        return {
          err: 1,
          msg: "登录已失效，请重新登录",
          data: response?.data,
        };
      }

      // 重试次数+1
      config.options.times = times + 1;

      // 重新发起请求
      return request(config);
    } else {
      // 重新获取 accessToken 失败，重新登录
      handleLogout();
      error.message = error.msg = "登录已失效，请重新登录";
    }
  }
}
